import { StandardProps } from "@/components/componentMap.ts";
import { useEffect, useRef, useState } from "react";
import { AddToBasketButton } from "./AddToBasketButton/AddToBasketButton";
import { useVariant } from "@/hooks/useVariant/useVariant.ts";
import { useScrollToButton } from "./hooks/useScrollToButton";
import { ExpressCheckoutButton } from "./ExpressCheckoutButton/ExpressCheckoutButton";
import { useButtonStates } from "./hooks/useButtonStates";
import { useBasket } from "./hooks/useBasket";
import { SIZE_SELECTOR_ERROR_NO_SIZE_SELECTED } from "@/constants/constants.ts";
import { handleBasketSubmission } from "./utils/handleBasketSubmission";
import { APIError } from "./types";
import { ErrorMessage } from "./ErrorMessage/ErrorMessage";
import { ProductBasketItems } from "./ProductBasketItems/ProductBasketItems";
import { ensureIsActiveVariant } from "@/utils/product/ensureIsActive.ts";
import { useSharedServices } from "@/utils/services/useSharedServices.ts";
import { Recommendations } from "@/components";

import styles from "./AddToBasketLocal.module.scss";

export const AddToBasketLocal = (props: StandardProps) => {
  const { product, configProps, flags, runtimeConfig } = props;

  const a2bRef = useRef<HTMLDivElement>(null);
  const addToBasketButtonRef = useRef<HTMLButtonElement>(null);
  const { variant, isSelected } = useVariant(product);
  const activeVariant = ensureIsActiveVariant(variant);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExpressSubmitting, setIsExpressSubmitting] = useState(false);
  const [addToBasketSubmissionError, setAddToBasketSubmissionError] = useState<
    APIError | undefined
  >();

  const { basket, setBasket } = useBasket(
    runtimeConfig.basketApiBaseUrl,
    product.aliases.pimProductId,
  );

  const [apiErrorState, setApiErrorState] = useState({
    addToBasketDisabled: false,
    expressCheckoutDisabled: false,
    buttonText: "Add to basket",
  });

  const buttonStates = useButtonStates({
    product,
    variant,
    isSelected,
    configProps,
    flags,
    isSubmitting,
    isExpressSubmitting,
    apiErrorState,
  });

  const [services] = useSharedServices([]);

  const basketApiBaseUrl = runtimeConfig.basketApiBaseUrl;
  const basketCheckoutHost = runtimeConfig.basketCheckoutHost;

  useScrollToButton(product, a2bRef);

  useEffect(() => {
    setAddToBasketSubmissionError(undefined);
    setApiErrorState({
      addToBasketDisabled: false,
      expressCheckoutDisabled: false,
      buttonText: "Add to basket",
    });
  }, [variant.id]);

  const focusAddToBasketButton = () => {
    addToBasketButtonRef.current?.focus();
  };

  const handleAddToBasketClick =
    (isExpressCheckout = false) =>
    async () => {
      if (!isSelected) {
        window.dispatchEvent(
          new CustomEvent(SIZE_SELECTOR_ERROR_NO_SIZE_SELECTED),
        );
        return;
      }

      if (!activeVariant) {
        return;
      }

      await handleBasketSubmission(
        {
          basketApiBaseUrl,
          basketCheckoutHost,
          pimProductId: product.aliases.pimProductId,
          skuId: activeVariant.aliases.skuId,
          variantId: activeVariant.id,
          isExpressCheckout,
          isShipFromStore: activeVariant.availability.shipFromStore === true,
          price: activeVariant.price.value,
          title: product.title,
          services,
        },
        {
          setIsSubmitting,
          setIsExpressSubmitting,
          setAddToBasketSubmissionError,
          setBasket,
          setButtonState: setApiErrorState,
          focusAddToBasketButton,
        },
      );
    };

  if (!buttonStates.shouldRenderSection) {
    return null;
  }

  return (
    <div ref={a2bRef} className={styles.container} id="add-to-basket-button">
      {addToBasketSubmissionError && (
        <ErrorMessage error={addToBasketSubmissionError} />
      )}
      {buttonStates.expressCheckout.visible && (
        <ExpressCheckoutButton
          disabled={!buttonStates.expressCheckout.enabled}
          asPrimaryButton={!buttonStates.addToBasket.visible}
          isShipFromStore={variant.availability?.shipFromStore === true}
          handleAddToBasketClick={handleAddToBasketClick(true)}
          isSubmitting={isExpressSubmitting}
        />
      )}
      {buttonStates.addToBasket.visible && (
        <AddToBasketButton
          ref={addToBasketButtonRef}
          disabled={!buttonStates.addToBasket.enabled}
          isSubmitting={isSubmitting}
          handleAddToBasketClick={handleAddToBasketClick()}
          buttonText={buttonStates.addToBasket.text}
        />
      )}
      {basket.length > 0 && (
        <div className={styles.confirmationBox}>
          <ProductBasketItems basket={basket} />
          <Recommendations
            {...props}
            configProps={{
              placementId:
                props.configProps?.recsPlacementId || "OSPStandardA2BClick",
              maxProductsPerPage: 2,
            }}
          />
        </div>
      )}
    </div>
  );
};
